/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Constants } from '../../../../core/constants';
import { PCState } from '../../../../core/enums';
import { ServicesClientImpl } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { ScreenTaskControllerTypes } from '../../../../core/navigation/ScreenTaskControllerTypes';
import {
    handlePCScreenVerifiedPin,
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showCreatePinOvelay,
    showDisablePCPinOverlay,
    showEnterPCPinOverlay,
    showPCCurrentPinOverlay,
} from '../../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    showHideAdultContentScreen,
    showMainSettingsScreen,
    showSetRatingLimitsScreen,
} from '../../../screens/screens.reducers';
import { setPCSettingsFocusedPositionAction, setPCStateAction } from '../parentalcontrols.actions';

const onKeyBackDefaultPCSettings = () => (dispatch: Function) => {
    dispatch(showMainSettingsScreen());
    dispatch(setPCSettingsFocusedPositionAction(0, true));
};

const onKeyDownDefaultPCSettings = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, verticalLimit } = getState().parentalControls;
    const newVerticalPosition = verticalPosition + 1;
    if (newVerticalPosition < verticalLimit) {
        dispatch(setPCSettingsFocusedPositionAction(newVerticalPosition, true));
    }
};

const onKeyUpDefaultPCSettings = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition } = getState().parentalControls;
    if (verticalPosition > 0) {
        const newVerticalPosition = verticalPosition - 1;
        dispatch(setPCSettingsFocusedPositionAction(newVerticalPosition, true));
    }
};

const onKeyOkDefaultPCSettings = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, menuOptions } = getState().parentalControls;

    const services = new ServicesClientImpl();
    const currentPCState = services.getPCState();

    if (currentPCState === PCState.Off) {
        dispatch(showCreatePinOvelay());
        return;
    }

    switch (menuOptions[verticalPosition]?.title) {
        case Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT):
            dispatch(onKeyOkParentalControls());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_PC_HIDE_ADULT_COTENT_TEXT):
            if (currentPCState === PCState.Unlocked) {
                dispatch(showHideAdultContentScreen());
            } else {
                dispatch(showEnterPCPinOverlay(handlePCScreenVerifiedPin));
            }
            break;

        case Locale.getString(LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_TEXT):
            if (currentPCState === PCState.Unlocked) {
                dispatch(showSetRatingLimitsScreen());
            } else {
                dispatch(showEnterPCPinOverlay(handlePCScreenVerifiedPin));
            }
            break;
        case Locale.getString(LocaleKeys.SETTINGS_PC_DISABLE_PARENTAL_CONTROLS_TEXT):
            dispatch(showDisablePCPinOverlay(handlePCScreenVerifiedPin));
            break;

        case Locale.getString(LocaleKeys.SETTINGS_PC_CHANGE_PIN_TEXT):
            dispatch(showPCCurrentPinOverlay(handlePCScreenVerifiedPin));
            break;

        default:
    }
};

export const onKeyDigitPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;

        default:
    }
};

const onKeyOkParentalControls = () => (dispatch: Function) => {
    const services = new ServicesClientImpl();
    const pcState = services.getPCState();

    switch (pcState) {
        case PCState.Locked:
            // Confirming Pin for turning off the PC.
            dispatch(showEnterPCPinOverlay(handlePCScreenVerifiedPin));
            break;

        case PCState.Unlocked:
            // No PIN Overlay for locking.
            services.updatePCState(PCState.Locked);
            dispatch(setPCStateAction(PCState.Locked));
            break;

        default:
    }
};

export const onKeyBackPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_DEFAULT_TASKS:
            dispatch(onKeyBackDefaultPCSettings());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;

        default:
    }
};

export const onKeyRightPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;

        default:
    }
};

export const onKeyLeftPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;

        default:
    }
};

export const onKeyDownPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_DEFAULT_TASKS:
            dispatch(onKeyDownDefaultPCSettings());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;

        default:
    }
};

export const onKeyOkPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_DEFAULT_TASKS:
            dispatch(onKeyOkDefaultPCSettings());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;

        default:
    }
};

export const onKeyUpPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_DEFAULT_TASKS:
            dispatch(onKeyUpDefaultPCSettings());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;

        default:
    }
};

export const onMouseOverPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_DEFAULT_TASKS:
            dispatch(onMouseOutAndOverPCDefaultTask(true));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        default:
    }
};

export const onMouseOutPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_DEFAULT_TASKS:
            dispatch(onMouseOutAndOverPCDefaultTask(false));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        default:
    }
};

const onMouseOutAndOverPCDefaultTask =
    (isFocused: boolean) => (dispatch: Function, getState: Function) => {
        const { inputFactory } = getState();
        const [focusItemIndexStr] = inputFactory.value.split('-');
        const focusItemIndex = Number(focusItemIndexStr);

        if (Number.isNaN(focusItemIndex)) return;

        dispatch(setPCSettingsFocusedPositionAction(focusItemIndex, isFocused));
    };

export const onMouseClickPCSettings = () => (dispatch: Function, getState: Function) => {
    const { screens, inputFactory } = getState();
    const { screenTaskController } = screens;
    const { value } = inputFactory;

    if (value === Constants.VIDEO_PLAYER_UI_ID) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_DEFAULT_TASKS:
            dispatch(onKeyOkPCSettings());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        default:
    }
};
